import { getI18n } from 'react-i18next';
import {
  KingOfTheHillSpecificDetailsFragment,
  KothDetailsForAllEventsFragment,
  MatchUpFormat,
  MatchUpFragment,
  MatchUpTeamParticipantFragment,
  MatchUpIndividualParticipantFragment,
  MatchUpPhaseFragment,
  MatchUpRoundFragment,
  PlayableMatchUpMatchFragment,
} from 'src/generated/graphql';
import { isTypename, isTypenameCallback } from 'src/utilities/TSUtilities';
import {
  getIndividualParticipantsFromParticipantReference,
  getTeamParticipantsFromParticipantReference,
} from '@pages/Events/EventUtilities';
import {
  createFakeMatchUpFromUpcomingKOTHParticipant,
  getNextUpcomingKOTHParticipant,
} from '../../../Tabs/ScheduleTab/ScheduleTabHelpers';

export const FAKE_PHASE_ID = 'FAKE_PHASE_ID';
export const formatMatchUpData = (
  matchUp: MatchUpFragment
): MatchUpFragment => {
  const upcomingParticipant = getNextUpcomingKOTHParticipant(matchUp);

  /**
   * If there is an upcoming participant, create a new fake phase with a fake match
   * for that upcoming participant.
   */
  if (
    upcomingParticipant &&
    matchUp.matchUpFormatSpecificDetails &&
    isTypename(
      'KingOfTheHillSpecificDetails',
      matchUp.matchUpFormatSpecificDetails
    ) &&
    (isTypename('TeamParticipant', matchUp.matchUpFormatSpecificDetails.host) ||
      isTypename(
        'IndividualParticipant',
        matchUp.matchUpFormatSpecificDetails.host
      ))
  ) {
    matchUp.phases = [
      ...matchUp.phases,
      {
        id: FAKE_PHASE_ID,
        isComplete: false,
        phaseNumber: matchUp.phases.length + 1,
        matches: [
          createFakeMatchUpFromUpcomingKOTHParticipant(
            upcomingParticipant,
            matchUp.matchUpFormatSpecificDetails.host
          ),
        ],
        __typename: 'MatchUpPhase',
      },
    ];
  }

  return matchUp;
};

/**
 * Yeah, this looks (and probably is) stupid.
 *
 * I haven't figured out yet how to narrow nested types. So in this case,
 * we want a host that is a TeamParticipant on a MatchUpFormatSpecificDetails
 * that is a KingOfTheHillSpecificDetails, on a Competition that is a MatchUp.
 *
 * Ideally we could do all those checks and just return the Competition, and our
 * code would know when we try to access, but Typescript doesn't play nice with nested
 * discriminated unions.
 *
 * Rather than muddy up our component code with all these checks, I'm putting them here,
 * and will hopefully either figure out a better way with Typescript, or refactor our
 * code so we do these checks sooner than in the component, by just passing in the nested
 * parts as props that are already narrowed..
 */
export const getKingOfTheHillHost = (
  competition: MatchUpFragment
):
  | MatchUpTeamParticipantFragment
  | MatchUpIndividualParticipantFragment
  | null =>
  isTypename('MatchUp', competition) &&
  competition.matchUpFormatSpecificDetails &&
  isTypename(
    'KingOfTheHillSpecificDetails',
    competition.matchUpFormatSpecificDetails
  ) &&
  (isTypename(
    'TeamParticipant',
    competition.matchUpFormatSpecificDetails.host
  ) ||
    isTypename(
      'IndividualParticipant',
      competition.matchUpFormatSpecificDetails.host
    ))
    ? competition.matchUpFormatSpecificDetails.host
    : null;

export const getInProgressMatchForUser = (
  userId: string,
  matchUp: MatchUpFragment
): PlayableMatchUpMatchFragment | null =>
  matchUp.phases
    // Don't want completed phases.
    .filter((phase) => !phase.isComplete)
    // Get all matches from all phases
    .flatMap((phase) => phase.matches)
    // Narrow type to PlayableMatchUpMatch only (don't care about Byes)
    .filter(isTypenameCallback('PlayableMatchUpMatch'))
    // Get the first (and should be only) match that the current user is a participant of.
    .find((match) =>
      match.rounds.find(
        (round) =>
          getTeamParticipantsFromParticipantReference(round.participants).find(
            (participant) =>
              participant.teamMembers.find((teamMember) =>
                Boolean(teamMember.person?.id === userId)
              )
          ) ||
          getIndividualParticipantsFromParticipantReference(
            round.participants
          ).find((participant) => Boolean(participant.person?.id === userId))
      )
    ) ?? null;

// todo check with @brody/@martin if this should maybe be a filter and not a find as a user could potentially be in more than one match
// and we should return an array instead of a single match this utility function right now is only used to highlight a group so a user knows which one to click on
// if they are not coming in directly from the join match now button
export const getMatchForUserInMatchUpPhase = (
  userId: string,
  matchUpPhase: MatchUpPhaseFragment
): PlayableMatchUpMatchFragment | null =>
  matchUpPhase.matches
    .filter(isTypenameCallback('PlayableMatchUpMatch'))
    .find((match) =>
      match.rounds.find(
        (round) =>
          getTeamParticipantsFromParticipantReference(round.participants).find(
            (participant) =>
              participant.teamMembers.find((teamMember) =>
                Boolean(teamMember.person?.id === userId)
              )
          ) ||
          getIndividualParticipantsFromParticipantReference(
            round.participants
          ).find((participant) => Boolean(participant.person?.id === userId))
      )
    ) ?? null;

/**
 * Check if the user with the given accountId has already
 * completed their round in the matchup.
 *
 * Makes a couple of assumptions.
 * 1. There aren't Byes in a KOTH challenge.
 * 2: That a user only plays a single match.
 */
export function userHasAlreadyPlayedInMatchUp(
  matchUp: MatchUpFragment,
  accountId: string
) {
  return matchUp.phases.some((phase) =>
    phase.matches
      .filter(isTypenameCallback('PlayableMatchUpMatch'))
      .flatMap((match) => match.rounds)
      .some(
        (round) =>
          round.isComplete &&
          (getTeamParticipantsFromParticipantReference(round.participants)
            .flatMap((participant) => participant.teamMembers)
            .some((teamMember) => teamMember.person?.id === accountId) ||
            getIndividualParticipantsFromParticipantReference(
              round.participants
            ).some((participant) => participant.person?.id === accountId))
      )
  );
}

export function sumOfAllRoundDimensionsForParticipantId(
  rounds: MatchUpRoundFragment[],
  dimensionList: string[],
  participantId: string
) {
  const participantResults = rounds.flatMap((round) => {
    const participant = round.participants.find(
      (participant) => participant.participant.id === participantId
    );

    return participant?.participant && participant.participant.result;
  });

  const flattenDimensions = participantResults.flatMap((result) => {
    if (result && isTypename('MultiDimensionalBucketBasedResult', result)) {
      return result.multiDimensionalResults;
    }

    return null;
  });

  const aggregateOfRoundDimensionList = dimensionList.map((dimensionKey) =>
    flattenDimensions.filter(
      (dimension) => dimension?.dimensionId === dimensionKey
    )
  );

  if (
    aggregateOfRoundDimensionList.length > 0 &&
    aggregateOfRoundDimensionList[0].length > 0
  ) {
    return aggregateOfRoundDimensionList.map((aggregateList) =>
      aggregateList.length > 0
        ? aggregateList.reduce((acc, curr) => ({
            dimensionId: curr?.dimensionId ?? '',
            value: (acc?.value ?? 0) + (curr?.value ?? 0),
            __typename: 'SingleDimensionResult',
          }))
        : null
    );
  }

  return [];
}

export function getAverageInGamePlacementForParticipant(
  rounds: MatchUpRoundFragment[],
  participantId: string
) {
  let numberOfRoundsWithPlacement = 0;
  const accumulatedRoundPlacements = rounds
    .flatMap((round) => {
      const participant = round.participants.find(
        (participant) => participant.participant.id === participantId
      );

      if (
        participant?.participant &&
        !isTypename(
          'MaximumParticipantsBreakStrategy',
          participant.participant
        ) &&
        participant.participant.result &&
        isTypename(
          'MultiDimensionalBucketBasedResult',
          participant.participant.result
        ) &&
        participant.participant.result.bucketValue
      ) {
        numberOfRoundsWithPlacement++;
        return participant.participant.result.bucketValue;
      } else if (
        participant?.participant &&
        !isTypename(
          'MaximumParticipantsBreakStrategy',
          participant.participant
        ) &&
        participant.participant.result &&
        isTypename('BasicResult', participant.participant.result) &&
        participant.participant.result.placement
      ) {
        numberOfRoundsWithPlacement++;
        return participant.participant.result.placement;
      }

      return 0;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue);

  // guard against NaN
  numberOfRoundsWithPlacement =
    numberOfRoundsWithPlacement === 0 ? 1 : numberOfRoundsWithPlacement;

  return Math.round(accumulatedRoundPlacements / numberOfRoundsWithPlacement);
}

export function accountIsMatchUpHost(
  matchUpData: {
    __typename?: 'MatchUp';
    matchUpFormatSpecificDetails?:
      | KingOfTheHillSpecificDetailsFragment
      | KothDetailsForAllEventsFragment;
  },
  accountId: string
): boolean {
  if (
    matchUpData.matchUpFormatSpecificDetails?.host &&
    isTypename('TeamParticipant', matchUpData.matchUpFormatSpecificDetails.host)
  ) {
    return Boolean(
      matchUpData.matchUpFormatSpecificDetails.host.teamMembers.find(
        (member) => member.person?.id === accountId
      )
    );
  }

  if (
    matchUpData.matchUpFormatSpecificDetails?.host &&
    (isTypename(
      'ReadyIndividualParticipant',
      matchUpData.matchUpFormatSpecificDetails.host
    ) ||
      isTypename(
        'UnreadyIndividualParticipant',
        matchUpData.matchUpFormatSpecificDetails.host
      ))
  ) {
    return Boolean(
      matchUpData.matchUpFormatSpecificDetails.host.person?.id === accountId
    );
  }

  return false;
}

export const getMatchUpFormats = () => {
  const i18n = getI18n();
  return {
    [MatchUpFormat.KingOfTheHill]: i18n.t(
      'match-up-format.king-of-the-hill',
      'King of the Hill'
    ),
    [MatchUpFormat.BattleRoyale]: i18n.t(
      'match-up-format.match-up',
      'Battle Royale'
    ),
  };
};

export const getMatchUpFormat = (matchUp: {
  __typename: string;
  matchUpFormat: MatchUpFormat;
}) => {
  if (matchUp.__typename !== 'MatchUp') {
    return '--';
  }

  return getMatchUpFormats()[matchUp.matchUpFormat];
};
